import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, TrendingUp, Zap, Check, Menu, X } from "lucide-react";
import { Button } from "../components/ui/button";
import OpportunityCompactCard from "../components/opportunities/OpportunityCompactCard";
import axios from "axios";
import { useAuth } from "../App";

const BACKEND_ORIGIN = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/$/, "");
const publicClient = axios.create({
  baseURL: BACKEND_ORIGIN ? `${BACKEND_ORIGIN}/api` : "/api",
  withCredentials: true,
});
publicClient.interceptors.request.use((config) => {
  config.headers["X-Requested-With"] = "XMLHttpRequest";
  return config;
});

const LANDING_FEATURED_CACHE_KEY = "landing_featured_opportunities_v1";
const PUBLIC_IDEA_USED_KEY = "sr_public_idea_used_v1";
const IDEA_MIN_CHARS = 10;
const IDEA_MAX_CHARS = 500;
const TARGET_MVP_COUNT = 526;
const LANDING_FALLBACK_OPPORTUNITIES = [
  {
    opportunity_id: "fallback-ai-meeting-notes",
    niche: "Sales",
    trend_status: "rising",
    name: "AI Meeting Notes for SMB Sales Teams",
    short_description: "Capture calls, extract action items, and sync CRM fields automatically.",
    opportunity_score: 82,
    momentum: 0.22,
    trend_score: 72,
    estimated_mrr_min: 2800,
    estimated_mrr_max: 12000,
    competition: "Medium",
    time_to_mvp: "2–3 months",
  },
  {
    opportunity_id: "fallback-whatsapp-crm",
    niche: "Local Commerce",
    trend_status: "underrated",
    name: "WhatsApp CRM for Local Businesses",
    short_description: "Unified inbox, lead tagging, and follow-up automation for WhatsApp leads.",
    opportunity_score: 76,
    momentum: 0.18,
    trend_score: 68,
    estimated_mrr_min: 1500,
    estimated_mrr_max: 8500,
    competition: "Low",
    time_to_mvp: "6–8 weeks",
  },
  {
    opportunity_id: "fallback-finops-smb",
    niche: "Finance Ops",
    trend_status: "cooling",
    name: "SMB Cloud FinOps Copilot",
    short_description: "Detect cloud cost leaks and recommend right-sizing actions for lean teams.",
    opportunity_score: 71,
    momentum: -0.04,
    trend_score: 55,
    estimated_mrr_min: 4000,
    estimated_mrr_max: 18000,
    competition: "Medium",
    time_to_mvp: "3–4 months",
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [featured, setFeatured] = useState(() => {
    try {
      const raw = localStorage.getItem(LANDING_FEATURED_CACHE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed?.data) && parsed.data.length > 0) {
        return parsed.data.slice(0, 3);
      }
    } catch {
      /* ignore cache parse errors */
    }
    return LANDING_FALLBACK_OPPORTUNITIES;
  });
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const [mvpCounter, setMvpCounter] = useState(0);
  const [counterReached, setCounterReached] = useState(false);
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
  const [ideaInput, setIdeaInput] = useState("");
  const [ideaAnalysis, setIdeaAnalysis] = useState(null);
  const [ideaAnalysisLoading, setIdeaAnalysisLoading] = useState(false);
  const [ideaAnalysisError, setIdeaAnalysisError] = useState("");
  const safeFeatured = Array.isArray(featured) ? featured : [];
  const displayedFeatured =
    safeFeatured.length > 0 ? safeFeatured.slice(0, 3) : LANDING_FALLBACK_OPPORTUNITIES;

  useEffect(() => {
    let isMounted = true;
    setFeaturedLoading(true);
    publicClient
      .get("/opportunities/featured?limit=3&include_market_metrics=true")
      .then((res) => {
        if (!isMounted) return;
        const payload = res.data;
        const opportunities = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.opportunities)
          ? payload.opportunities
          : Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload?.data)
          ? payload.data
          : [];

        const nextFeatured = opportunities.slice(0, 3);
        if (nextFeatured.length > 0) {
          setFeatured(nextFeatured);
          try {
            localStorage.setItem(
              LANDING_FEATURED_CACHE_KEY,
              JSON.stringify({ data: nextFeatured, ts: Date.now() })
            );
          } catch {
            /* ignore cache write errors */
          }
        }
      })
      .catch(() => {
        if (!isMounted) return;
        // Keep already-rendered fallback/cache so section never appears empty.
      })
      .finally(() => {
        if (isMounted) {
          setFeaturedLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (user) {
      navigate("/dashboard/opportunities", { replace: true });
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    let frameId = null;
    const animationDurationMs = 2200;
    const animationStart = performance.now();

    const animateCounter = (now) => {
      const progress = Math.min((now - animationStart) / animationDurationMs, 1);
      const eased = 1 - (1 - progress) ** 3;
      setMvpCounter(Math.round(TARGET_MVP_COUNT * eased));
      if (progress < 1) {
        frameId = window.requestAnimationFrame(animateCounter);
      }
    };

    frameId = window.requestAnimationFrame(animateCounter);
    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  useEffect(() => {
    if (mvpCounter >= TARGET_MVP_COUNT && !counterReached) {
      setCounterReached(true);
    }
  }, [mvpCounter, counterReached]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveFeatureIndex((prev) => (prev + 1) % 3);
    }, 1800);
    return () => window.clearInterval(intervalId);
  }, []);

  const features = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Discover",
      description: "Browse curated SaaS opportunities with real market data and trend analysis"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Analyze",
      description: "Deep-dive into competition, monetization strategies, and revenue potential"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Build",
      description: "Get AI-powered guidance to turn ideas into MVPs with actionable plans"
    }
  ];

  const handleAnalyzeIdea = async () => {
    const idea = ideaInput.trim();
    if (!idea) return;

    try {
      if (!user && typeof window !== "undefined" && window.localStorage?.getItem(PUBLIC_IDEA_USED_KEY) === "1") {
        navigate("/login", { replace: true });
        return;
      }
    } catch {
      /* ignore */
    }

    if (idea.length < IDEA_MIN_CHARS) {
      setIdeaAnalysisError(`Use at least ${IDEA_MIN_CHARS} characters to describe your SaaS idea.`);
      return;
    }
    if (idea.length > IDEA_MAX_CHARS) {
      setIdeaAnalysisError(`Keep your idea under ${IDEA_MAX_CHARS} characters.`);
      return;
    }

    setIdeaAnalysisLoading(true);
    setIdeaAnalysisError("");
    setIdeaAnalysis(null);
    try {
      const { data } = await publicClient.post("/public/analyze-idea", { idea });
      const score = Number(data?.score) || 0;
      const tone = score >= 78 ? "text-emerald-300" : score >= 60 ? "text-amber-200" : "text-zinc-300";
      const revenueLine = data?.revenue ?? data?.monetization ?? "";
      setIdeaAnalysis({ ...data, revenue: revenueLine, score, tone });
      if (!user) {
        try {
          window.localStorage?.setItem(PUBLIC_IDEA_USED_KEY, "1");
        } catch {
          /* ignore */
        }
      }
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 403 && detail && typeof detail === "object" && detail.code === "PUBLIC_IDEA_REQUIRES_LOGIN") {
        navigate("/login", { replace: true });
        return;
      }
      const message = Array.isArray(detail)
        ? detail.map((d) => d?.msg || "").filter(Boolean).join(" ") || "Could not analyze this idea."
        : typeof detail === "string"
        ? detail
        : typeof detail === "object" && detail?.message
        ? String(detail.message)
        : status === 429
        ? "Too many requests. Please wait or sign in."
        : error?.message === "Network Error"
        ? "Cannot reach the API. Check REACT_APP_BACKEND_URL and CORS."
        : "Idea analysis is temporarily unavailable. Please try again later.";
      setIdeaAnalysisError(message);
    } finally {
      setIdeaAnalysisLoading(false);
    }
  };

  // Live featured opportunities come from backend (state: `featured`)

  const pricingPlans = [
    {
      name: "Free Access",
      price: "€0",
      period: "",
      features: [
        "Browse SaaS already making money",
        "Access basic analytics & trends",
        "Discover validated business ideas",
        "Understand how they make revenue",
        "1 AI idea analysis",
      ],
      cta: "Try AI analysis free",
      highlighted: false,
      anchor: "idea-check",
    },
    {
      name: "SaaS Radar Pro",
      price: "€19",
      period: "/month",
      yearly: "€190/year (2 months free)",
      features: [
        "AI Cofounder: turn ideas into SaaS plans instantly",
        "Full access to SaaS data, analytics & trends",
        "See real revenue, growth & opportunities",
        "Find winning ideas faster with smart filters",
        "Unlimited AI idea analysis",
        "Step-by-step plan to build your MVP",
        "Get execution insights and hidden opportunities",
        "Priority support",
      ],
      cta: "Start Pro",
      highlighted: true,
    },
  ];

  const testimonials = [
    {
      quote:
        "I used to waste hours checking random forums. With SaaS-Radar, I can spot what is actually gaining traction in one place.",
      name: "Lina",
      role: "Indie founder",
    },
    {
      quote:
        "The opportunity scoring helped me stop second-guessing every idea. I finally picked one niche and started building.",
      name: "Marco",
      role: "Solo builder",
    },
    {
      quote:
        "What I like is that it feels practical. Not hype, just clear signals and a roadmap I can execute.",
      name: "Samira",
      role: "Product freelancer",
    },
    {
      quote:
        "I validated my current project idea in a weekend. Before that, I spent weeks bouncing between random tools.",
      name: "Yann",
      role: "Early-stage founder",
    },
    {
      quote:
        "The momentum signals saved me from building in a dead niche. That alone made it worth using.",
      name: "Nora",
      role: "Startup operator",
    },
    {
      quote:
        "It feels like a practical radar, not just another dashboard. I open it every morning before planning my day.",
      name: "Tobias",
      role: "Bootstrapped founder",
    },
    {
      quote:
        "I like that it pushes me to ship faster. I stopped overthinking and started executing.",
      name: "Imane",
      role: "Product manager",
    },
    {
      quote:
        "The quality of opportunities is better than what I got from Twitter threads and random communities.",
      name: "Leo",
      role: "Indie hacker",
    },
  ];
  const testimonialsRowA = [...testimonials, ...testimonials];
  const testimonialsRowB = [...testimonials.slice().reverse(), ...testimonials.slice().reverse()];

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#0a0a0a]">
      <div className="landing-page-atmosphere" aria-hidden="true">
        <div className="landing-page-aurora-grid" />
        <div className="landing-orb landing-orb-primary" />
        <div className="landing-orb landing-orb-secondary" />
        <div className="landing-orb landing-orb-tertiary" />
        <div className="landing-light-sweep" />
        <div className="landing-noise-overlay" />
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/45 backdrop-blur-xl border-b border-[#1F1F1F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              {logoBroken ? (
                <div className="w-8 h-8 rounded-lg bg-[#7C3AED] flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-black" />
                </div>
              ) : (
                <img
                  src="/logo.png"
                  alt="SaaS-Radar"
                  className="h-8 w-8 rounded-lg object-contain"
                  onError={() => setLogoBroken(true)}
                />
              )}
              <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                SaaS-Radar
              </span>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#idea-check" className="text-zinc-400 hover:text-white transition-colors text-sm">Idea Check</a>
              <a href="#features" className="text-zinc-400 hover:text-white transition-colors text-sm">Features</a>
              <a href="#opportunities" className="text-zinc-400 hover:text-white transition-colors text-sm">Opportunities</a>
              <a href="#pricing" className="text-zinc-400 hover:text-white transition-colors text-sm">Pricing</a>
            </nav>

            <div className="hidden md:flex items-center gap-4">
              <Button 
                variant="ghost" 
                className="text-zinc-400 hover:text-white"
                onClick={() => navigate("/auth")}
                data-testid="header-login-btn"
              >
                Log in
              </Button>
              <Button 
                className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-full px-6"
                onClick={() => navigate("/auth")}
                data-testid="header-signup-btn"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-zinc-400 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0a0a0a]/95 backdrop-blur-xl border-t border-[#1F1F1F]">
            <div className="px-4 sm:px-6 py-4 space-y-4">
              <a href="#idea-check" className="block text-zinc-400 hover:text-white">Idea Check</a>
              <a href="#features" className="block text-zinc-400 hover:text-white">Features</a>
              <a href="#opportunities" className="block text-zinc-400 hover:text-white">Opportunities</a>
              <a href="#pricing" className="block text-zinc-400 hover:text-white">Pricing</a>
              <div className="pt-4 space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full border-[#1F1F1F]"
                  onClick={() => navigate("/auth")}
                >
                  Log in
                </Button>
                <Button 
                  className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold"
                  onClick={() => navigate("/auth")}
                >
                  Get Started
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="relative z-10 max-w-[100vw] overflow-x-hidden">
      {/* Hero Section (classic) */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-24 md:pt-40 md:pb-32 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid items-start gap-8 sm:gap-10 xl:gap-16 lg:grid-cols-[minmax(0,1fr)_640px]">
          <div className="max-w-3xl min-w-0">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.03] border border-[#1F1F1F] mb-6 sm:mb-8">
              <span className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-300">Now in Beta</span>
            </div>

            <h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-[5rem] font-bold tracking-tighter leading-[1.05] sm:leading-[1.1] text-white mb-5 sm:mb-6 opacity-0 animate-fade-in-up"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Find and build SaaS opportunities{" "}
              <span className="gradient-text-accent">before they go mainstream</span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-zinc-400 leading-relaxed mb-7 sm:mb-10 max-w-2xl opacity-0 animate-fade-in-up stagger-1">
              Spot validated SaaS ideas faster, understand demand in minutes, and launch with confidence.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-up stagger-2">
              <Button
                className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-full px-7 sm:px-8 py-5 sm:py-6 text-base sm:text-lg hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(255,106,0,0.3)] transition-all"
                onClick={() => navigate("/auth")}
                data-testid="hero-cta-btn"
              >
                Find and Build
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                variant="outline"
                className="bg-white/5 border-[#1F1F1F] text-white font-medium rounded-full px-7 sm:px-8 py-5 sm:py-6 text-base sm:text-lg hover:bg-white/10 transition-all"
                onClick={() => document.getElementById("idea-check")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-learn-more-btn"
              >
                Analyze Your Idea
              </Button>
            </div>

            <p className="mt-6 sm:mt-8 text-xs text-zinc-500 opacity-0 animate-fade-in-up stagger-3">
              Trusted by founders to move from idea to execution with clarity.
            </p>
          </div>
            <aside className="relative lg:pt-2 mx-auto w-full max-w-[680px]">
              <div className="relative transition-transform duration-500 hover:-translate-y-1">
                <img
                  src="/landing-hero-preview.png"
                  alt="SaaS-Radar market intelligence preview"
                  className="w-full object-cover"
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 sm:h-32 bg-gradient-to-b from-transparent to-[#0A0A0A]" />
              </div>
            </aside>
          </div>
          <div className="mt-5 sm:mt-8 text-center">
            <p
              className={`inline-flex items-baseline gap-2 hero-mvp-counter text-2xl sm:text-4xl font-bold tracking-tight ${
                counterReached ? "hero-mvp-counter-reached" : ""
              }`}
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              <span className="hero-mvp-counter-number">{mvpCounter}+</span>
              <span className="text-sm sm:text-lg font-medium text-[#C4B5FD]">MVP builds</span>
            </p>
          </div>
        </div>
      </section>
      <style>
        {`
          .hero-mvp-counter {
            animation: heroCounterFloat 3s ease-in-out infinite;
          }
          .hero-mvp-counter-number {
            background: linear-gradient(90deg, #c4b5fd 0%, #a78bfa 45%, #7c3aed 100%);
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
            text-shadow: 0 0 28px rgba(124, 58, 237, 0.35);
          }
          .hero-mvp-counter-reached {
            animation: heroCounterReachFlash 0.7s ease-out 1, heroCounterFloat 3s ease-in-out 0.7s infinite;
          }
          @keyframes heroCounterFloat {
            0%,
            100% {
              transform: translateY(0) scale(1);
              opacity: 0.92;
            }
            50% {
              transform: translateY(-3px) scale(1.04);
              opacity: 1;
            }
          }
          @keyframes heroCounterReachFlash {
            0% {
              transform: translateY(0) scale(1);
              filter: drop-shadow(0 0 0 rgba(167, 139, 250, 0));
              opacity: 0.9;
            }
            55% {
              transform: translateY(-2px) scale(1.08);
              filter: drop-shadow(0 0 18px rgba(167, 139, 250, 0.8));
              opacity: 1;
            }
            100% {
              transform: translateY(0) scale(1);
              filter: drop-shadow(0 0 0 rgba(167, 139, 250, 0));
              opacity: 1;
            }
          }
          .idea-focus-shell {
            transition: box-shadow 0.35s ease, transform 0.35s ease, border-color 0.35s ease;
          }
          .idea-focus-shell:focus-within {
            transform: scale(1.012);
            box-shadow:
              0 20px 50px -20px rgba(124, 58, 237, 0.35),
              0 0 0 1px rgba(167, 139, 250, 0.22),
              inset 0 1px 0 rgba(255, 255, 255, 0.06);
          }
          .idea-cta-btn {
            transition: transform 0.2s ease, box-shadow 0.25s ease, filter 0.2s ease;
          }
          .idea-cta-btn:hover:not(:disabled) {
            transform: translateY(-2px) scale(1.02);
            filter: brightness(1.06);
            box-shadow: 0 16px 40px -12px rgba(124, 58, 237, 0.55);
          }
          .idea-cta-btn:active:not(:disabled) {
            transform: translateY(0) scale(0.98);
          }
        `}
      </style>

      {/* Free Idea Check — minimal conversion block */}
      <section
        id="idea-check"
        className="relative overflow-hidden border-t border-white/[0.06] py-24 sm:py-32 md:py-40 px-5 sm:px-8"
      >
        <div className="pointer-events-none absolute inset-0">
          <div
            className="absolute left-1/2 top-[42%] h-[min(100vw,640px)] w-[min(100vw,640px)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-b from-[#7C3AED]/20 via-[#5B21B6]/10 to-transparent blur-3xl"
            aria-hidden
          />
          <div
            className="absolute left-1/2 top-[58%] h-[min(90vw,480px)] w-[min(90vw,480px)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-t from-[#0A0A0A] to-transparent opacity-90"
            aria-hidden
          />
        </div>

        <div className="relative z-10 mx-auto max-w-[34rem] text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-zinc-500">
            Free Idea Check
          </p>
          <h2
            className="mt-6 text-balance text-3xl font-semibold tracking-tight text-white sm:text-4xl md:text-[2.65rem] md:leading-[1.12]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Validate your SaaS idea instantly
          </h2>

          <div className="mt-14 sm:mt-16">
            <div className="idea-focus-shell rounded-[1.35rem] border border-white/[0.08] bg-white/[0.03] p-[1px] shadow-[0_12px_48px_-16px_rgba(0,0,0,0.65),0_0_0_1px_rgba(255,255,255,0.04)]">
              <textarea
                value={ideaInput}
                onChange={(e) => setIdeaInput(e.target.value)}
                placeholder="SaaS tool for remote team collaboration…"
                maxLength={IDEA_MAX_CHARS}
                rows={5}
                className="min-h-[148px] w-full resize-none rounded-[1.28rem] bg-[#0a0a0a]/90 px-6 py-5 text-left text-[15px] leading-[1.55] text-white shadow-inner outline-none placeholder:text-zinc-500 sm:min-h-[156px] sm:text-base"
                data-testid="landing-idea-input"
              />
            </div>

            <p className="mt-4 text-center text-[13px] font-medium tracking-wide text-zinc-500">
              No card required • Your idea is safe
            </p>

            <button
              type="button"
              className="idea-cta-btn mt-8 inline-flex h-14 w-full items-center justify-center rounded-full bg-gradient-to-r from-[#9D74F7] via-[#7C3AED] to-[#6D28D9] px-8 text-[17px] font-semibold text-white shadow-[0_10px_36px_-8px_rgba(124,58,237,0.55)] outline-none ring-offset-2 ring-offset-[#0a0a0a] transition-[transform,box-shadow,filter] focus-visible:ring-2 focus-visible:ring-[#A78BFA]/60 disabled:pointer-events-none disabled:opacity-45 sm:h-[3.75rem] sm:text-lg"
              onClick={handleAnalyzeIdea}
              disabled={
                ideaAnalysisLoading ||
                ideaInput.trim().length < IDEA_MIN_CHARS ||
                ideaInput.trim().length > IDEA_MAX_CHARS
              }
              data-testid="landing-idea-analyze-btn"
            >
              {ideaAnalysisLoading ? (
                <span className="flex w-full items-center justify-center gap-3">
                  <span className="h-5 w-5 shrink-0 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  <span>Analyzing…</span>
                </span>
              ) : (
                "Get My Answer →"
              )}
            </button>

            {ideaAnalysisError ? (
              <div className="mt-6 rounded-2xl border border-rose-500/20 bg-rose-500/[0.07] px-4 py-3 text-left text-sm leading-relaxed text-rose-100/95">
                {ideaAnalysisError}
              </div>
            ) : null}

            {ideaAnalysis ? (
              <div className="mt-10 rounded-[1.25rem] border border-white/[0.08] bg-black/30 p-6 text-left shadow-[0_16px_48px_-20px_rgba(0,0,0,0.5)] backdrop-blur-sm">
                <div className="mb-5 flex items-end justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">Score</p>
                    <p
                      className={`mt-1 text-3xl font-semibold ${ideaAnalysis.tone}`}
                      style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                    >
                      {ideaAnalysis.score}
                      <span className="text-lg font-normal text-zinc-500">/100</span>
                    </p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full border border-white/[0.08] bg-white/[0.05] px-3.5 py-1.5 text-xs font-semibold ${ideaAnalysis.tone}`}
                  >
                    {ideaAnalysis.verdict}
                  </span>
                </div>
                <div className="space-y-3 text-sm leading-relaxed text-zinc-400">
                  <p>
                    <span className="font-medium text-zinc-200">Market</span> — {ideaAnalysis.market}
                  </p>
                  <p>
                    <span className="font-medium text-zinc-200">Revenue</span> —{" "}
                    {ideaAnalysis.revenue || ideaAnalysis.monetization}
                  </p>
                  {Array.isArray(ideaAnalysis.next_steps) && ideaAnalysis.next_steps.length ? (
                    <div>
                      <p className="font-medium text-zinc-200">Next steps</p>
                      <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-zinc-400">
                        {ideaAnalysis.next_steps.map((step, index) => (
                          <li key={`${step}-${index}`}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  ) : null}
                  <p>
                    <span className="font-medium text-zinc-200">Competition</span> — {ideaAnalysis.competition}
                  </p>
                  <p>
                    <span className="font-medium text-zinc-200">Risk</span> — {ideaAnalysis.risks}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="idea-cta-btn mt-6 h-11 w-full rounded-full border-white/[0.12] bg-white/[0.04] text-sm font-medium text-white hover:bg-white/[0.08]"
                  onClick={() => navigate("/auth")}
                >
                  Open full radar
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="features" className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 lg:px-24 border-t border-[#1F1F1F]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4 block">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              From Discovery to Launch
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => (
              (() => {
                const isActive = index === activeFeatureIndex;
                return (
              <div 
                key={index}
                className={`bg-[#111111] border rounded-2xl md:rounded-[2rem] p-6 md:p-10 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group origin-center ${
                  isActive
                    ? "border-[#7C3AED]/55 scale-100 md:scale-[1.05] shadow-[0_26px_55px_rgba(124,58,237,0.25)] -translate-y-1"
                    : "border-[#1F1F1F] scale-[0.96] md:scale-[0.94] opacity-85"
                }`}
                data-testid={`feature-card-${index}`}
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 text-[#7C3AED] transition-all duration-500 ${
                  isActive ? "bg-[#7C3AED]/25 scale-110 rotate-[-3deg]" : "bg-[#7C3AED]/10"
                }`}>
                  {feature.icon}
                </div>
                <div className={`text-4xl font-bold mb-4 transition-colors duration-500 ${
                  isActive ? "text-[#BCA8FF]" : "text-zinc-700"
                }`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  0{index + 1}
                </div>
                <h3 className="text-xl md:text-2xl font-semibold tracking-tight text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {feature.title}
                </h3>
                <p className={`leading-relaxed transition-colors duration-500 ${
                  isActive ? "text-zinc-200" : "text-zinc-400"
                }`}>
                  {feature.description}
                </p>
              </div>
                );
              })()
            ))}
          </div>
        </div>
      </section>

      {/* Featured Opportunities Section */}
      <section id="opportunities" className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 lg:px-24 border-t border-[#1F1F1F]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Live Feed</span>
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#10B981]/15 border border-[#10B981]/30 text-[#34D399] text-[10px] font-bold uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#34D399] animate-pulse" />
                  Live
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                What's moving right now
              </h2>
              <p className="text-sm text-zinc-500 mt-2">
                Real opportunities from our ingestion pipeline — mix of rising, underrated, and cooling markets.
              </p>
            </div>
            <Button 
              variant="link" 
              className="text-[#A78BFA] hover:text-[#C4B5FD] mt-4 md:mt-0"
              onClick={() => navigate("/auth")}
              data-testid="view-all-opportunities-btn"
            >
              View All Opportunities
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3 lg:grid-cols-3">
            {displayedFeatured.map((opp, index) => (
              <OpportunityCompactCard
                key={opp.opportunity_id || index}
                opp={opp}
                dataTestId={`opportunity-preview-${index}`}
                onCardClick={() => navigate("/auth")}
                onWhyClick={(e) => {
                  e.stopPropagation();
                  navigate("/auth");
                }}
              />
            ))}
          </div>
          {featuredLoading ? <p className="mt-3 text-xs text-zinc-600">Updating live feed…</p> : null}
        </div>
      </section>

      {/* Cofounder preview */}
      <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 lg:px-24 border-t border-[#1F1F1F] relative">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4 block">Cofounder</span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Build faster with your AI cofounder
              </h2>
              <p className="text-lg text-zinc-400 leading-relaxed mb-8">
                Cofounder helps you turn opportunities into MVP plans, pricing angles, go-to-market steps, and more — like having a second founder in your corner, 24/7.
              </p>
              <ul className="space-y-4 mb-8">
                {["MVP feature planning", "Tech stack recommendations", "Pricing strategy", "First 10 customers strategy"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-zinc-300">
                    <div className="w-6 h-6 rounded-full bg-[#7C3AED]/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-[#7C3AED]" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <Button 
                className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-full px-8 py-4 md:py-6 w-full sm:w-auto"
                onClick={() => navigate("/auth")}
                data-testid="cofounder-cta-btn"
              >
                Try Cofounder Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <div className="relative">
              <div className="landing-ai-image-frame" />
              <div className="relative rounded-[1.6rem] border border-white/10 bg-[#0C0C0C]/70 p-2.5 sm:p-3 shadow-[0_24px_70px_rgba(0,0,0,0.5)] backdrop-blur-md transition-transform duration-500 hover:-translate-y-1">
                <img 
                  src="/landing-ai-preview.png"
                  alt="Cofounder preview"
                  className="w-full rounded-[1.2rem] relative z-10 border border-white/[0.08] object-cover"
                />
                <div className="pointer-events-none absolute inset-0 rounded-[1.6rem] ring-1 ring-inset ring-white/10" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials — clip marquee so the page never scrolls horizontally */}
      <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 lg:px-24 border-t border-[#1F1F1F] overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4 block">
              User Feedback
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold tracking-tight text-white"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              What founders say after using SaaS-Radar
            </h2>
          </div>

          <div className="space-y-6">
            <div className="overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
              <div className="testimonials-marquee-track-left flex w-max gap-6">
                {testimonialsRowA.map((item, index) => (
                  <div
                    key={`row-a-${index}`}
                    className="w-[340px] md:w-[420px] bg-[#111111] border border-[#1F1F1F] rounded-[1.6rem] p-6 md:p-7"
                  >
                    <p className="text-zinc-200 leading-relaxed mb-5">"{item.quote}"</p>
                    <p className="text-sm font-semibold text-white">{item.name}</p>
                    <p className="text-xs text-zinc-500">{item.role}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
              <div className="testimonials-marquee-track-right flex w-max gap-6">
                {testimonialsRowB.map((item, index) => (
                  <div
                    key={`row-b-${index}`}
                    className="w-[340px] md:w-[420px] bg-[#111111] border border-[#1F1F1F] rounded-[1.6rem] p-6 md:p-7"
                  >
                    <p className="text-zinc-200 leading-relaxed mb-5">"{item.quote}"</p>
                    <p className="text-sm font-semibold text-white">{item.name}</p>
                    <p className="text-xs text-zinc-500">{item.role}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 lg:px-24 border-t border-[#1F1F1F]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4 block">Pricing</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Simple, Transparent Pricing
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              Explore real SaaS traction for free — upgrade when you’re ready for Cofounder, unlimited analyzes, and pro signals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index}
                className={`rounded-2xl md:rounded-[2rem] p-6 md:p-10 ${
                  plan.highlighted 
                    ? 'bg-[#7C3AED]/10 border-2 border-[#7C3AED]/50 relative' 
                    : 'bg-[#111111] border border-[#1F1F1F]'
                }`}
                data-testid={`pricing-card-${plan.name.toLowerCase().replace(' ', '-')}`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-[#7C3AED] text-white text-xs font-bold px-4 py-1 rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      {plan.price}
                    </span>
                    {plan.period ? (
                      <span className="text-zinc-500">{plan.period}</span>
                    ) : null}
                  </div>
                  {plan.yearly && (
                    <p className="text-xs text-[#A78BFA] mt-2 font-medium">{plan.yearly}</p>
                  )}
                </div>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-zinc-300">
                      <Check className="w-5 h-5 text-[#7C3AED]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  className={`w-full rounded-full py-4 md:py-6 font-semibold ${
                    plan.highlighted 
                      ? 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white' 
                      : 'bg-white/5 border border-[#1F1F1F] text-white hover:bg-white/10'
                  }`}
                  onClick={() => plan.anchor ? document.getElementById(plan.anchor)?.scrollIntoView({ behavior: "smooth" }) : navigate("/auth")}
                  data-testid={`pricing-cta-${plan.name.toLowerCase().replace(' ', '-')}`}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 lg:px-24 border-t border-[#1F1F1F]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Ready to Find Your Next SaaS?
          </h2>
          <p className="text-lg text-zinc-400 mb-10 max-w-2xl mx-auto">
            Join thousands of founders discovering untapped opportunities and building profitable SaaS products.
          </p>
          <Button 
            className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-full px-10 py-6 text-lg hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(255,106,0,0.3)] transition-all"
            onClick={() => navigate("/auth")}
            data-testid="final-cta-btn"
          >
            Unlock SaaS Radar
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      </main>
    </div>
  );
};

export default LandingPage;
